import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Table, Input, InputNumber, Button, notification, Typography, Space, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { settingsService, ReferenceNumberSetting } from '../../services/settings';

const { Text } = Typography;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

const modules = [
  { key: 'task', label: 'Task' },
  { key: 'letter', label: 'Letter' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'quotation', label: 'Quotation' },
  { key: 'invoice', label: 'Invoice' },
];

export const ReferenceNumbersPage: React.FC = () => {
  const [activeModule, setActiveModule] = useState(modules[0].key);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ReferenceNumberSetting[]>([]);
  const [formatDraft, setFormatDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [year, setYear] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);
  const [nextNumber, setNextNumber] = useState<number | null>(1);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Reference Numbers
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchRows = async (moduleKey: string) => {
    try {
      setLoading(true);
      const data = await settingsService.getReferenceNumbers(moduleKey);
      setRows(data || []);
      if (data && data.length > 0) {
        setFormatDraft(data[0].format || '');
      } else {
        setFormatDraft('');
      }
    } catch {
      notification.error({
        message: 'Failed to load reference numbers',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(activeModule);
    resetAddRow();
  }, [activeModule]);

  const isValidFormat = (value: string) => value.trim().length > 0 && /\{0+\}/.test(value);

  const handleAddRow = async () => {
    if (!year || !month || !nextNumber) {
      notification.error({
        message: 'Missing required fields',
        description: 'Year, Month, and Next Number are required.',
      });
      return;
    }
    try {
      setAdding(true);
      if (!isValidFormat(formatDraft)) {
        notification.error({
          message: 'Invalid prefix format',
          description: 'Prefix format is required and must include a number token like {000} or {0000}.',
        });
        return;
      }
      await settingsService.updateReferenceNumbers({
        module: activeModule,
        prefix: '',
        format: formatDraft,
        year,
        month,
        next_number: nextNumber,
      });
      notification.success({
        message: 'Reference row added',
      });
      setYear(null);
      setMonth(null);
      setNextNumber(1);
      setShowAddRow(false);
      await fetchRows(activeModule);
    } catch {
      notification.error({
        message: 'Failed to add reference row',
      });
    } finally {
      setAdding(false);
    }
  };

  const resetAddRow = () => {
    setYear(null);
    setMonth(null);
    setNextNumber(1);
    setShowAddRow(false);
  };

  const buildSample = (formatValue: string, yearValue?: number | null, monthValue?: number | null, nextValue?: number | null) => {
    if (!formatValue || !yearValue || !monthValue || !nextValue) return '-';
    const date = new Date(yearValue, monthValue - 1, 1);
    const yy = String(date.getFullYear()).slice(-2);
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    let result = formatValue
      .replaceAll('{YYYY}', yyyy)
      .replaceAll('{YY}', yy)
      .replaceAll('{MM}', mm)
      .replaceAll('{DD}', dd);
    const match = result.match(/\{(0+)\}/);
    if (match) {
      const pad = match[1].length;
      result = result.replace(/\{0+\}/, String(nextValue).padStart(pad, '0'));
    }
    return result;
  };

  const columns = [
    {
      title: (
        <Space size={6}>
          <span>Format</span>
          <Tooltip
            title={
              <div>
                <div>Format supports tokens:</div>
                <div>{'{YYYY}'} {'{YY}'} {'{MM}'}</div>
                <div>{'{000}'} / {'{0000}'} for padded numbers</div>
                <div>Example: INV{'{YY}'}{'{MM}'}-{'{0000}'}</div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'format',
      key: 'format',
      width: 160,
      render: (_: string, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return (
            <Input
              placeholder="e.g. INV{YY}{MM}-{0000}"
              value={formatDraft}
              onChange={(event) => setFormatDraft(event.target.value)}
            />
          );
        }
        return record.format || '-';
      },
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 120,
      render: (value: number, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return (
            <InputNumber
              min={2000}
              max={2100}
              placeholder="Year"
              value={year ?? undefined}
              onChange={(val) => setYear(typeof val === 'number' ? val : null)}
            />
          );
        }
        return value;
      },
    },
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 120,
      render: (value: number, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return (
            <Select
              placeholder="Month"
              value={month ?? undefined}
              onChange={(val) => setMonth(val)}
              style={{ width: '100%' }}
              options={Array.from({ length: 12 }, (_, i) => ({
                value: i + 1,
                label: i + 1,
              }))}
            />
          );
        }
        return value;
      },
    },
    {
      title: 'Next Number',
      dataIndex: 'next_number',
      key: 'next_number',
      width: 160,
      render: (_: number, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return (
            <InputNumber
              min={1}
              placeholder="Next Number"
              value={nextNumber ?? undefined}
              onChange={(val) => setNextNumber(typeof val === 'number' ? val : null)}
              style={{ width: '100%' }}
            />
          );
        }
        return record.next_number;
      },
    },
    {
      title: 'Sample',
      dataIndex: 'sample',
      key: 'sample',
      render: (value: string, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return buildSample(formatDraft, year, month, nextNumber);
        }
        return value || '-';
      },
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: ReferenceNumberSetting) => {
        if (record.id === -1) {
          return (
            <Space>
              <Button type="primary" onClick={handleAddRow} loading={adding}>
                Save
              </Button>
              <Button onClick={resetAddRow}>Cancel</Button>
            </Space>
          );
        }
        return null;
      },
    },
  ];

  const dataSource = useMemo(() => {
    if (!showAddRow) return rows;
    return [
      {
        id: -1,
        module: activeModule,
        prefix: '',
        format: formatDraft,
        year: year ?? 0,
        month: month ?? 0,
        next_number: nextNumber ?? 1,
        sample: '',
      } as ReferenceNumberSetting,
      ...rows,
    ];
  }, [activeModule, month, nextNumber, rows, showAddRow, year]);

  return (
    <div>
      <div>
        <Text type="secondary">
          Format must include {'{000}'} or {'{0000}'} for the running number. Tokens: {'{YYYY}'} {'{YY}'} {'{MM}'}.
        </Text>
      </div>
      <div>
      </div>
      <Tabs
        activeKey={activeModule}
        onChange={(key) => setActiveModule(key)}
        items={modules.map((module) => ({
          key: module.key,
          label: module.label,
          children: (
            <div>
              <Space style={{ marginBottom: 12 }}>
                <Button onClick={() => setShowAddRow(true)} disabled={showAddRow}>
                  Add
                </Button>
              </Space>

              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={false}
              />
            </div>
          ),
        }))}
      />
    </div>
  );
};
